import React from 'react';
import { Modal, Card } from 'antd';
import { FileImageOutlined, VideoCameraOutlined, FileOutlined, EnvironmentOutlined } from '@ant-design/icons';

/**
 * 模板預覽 Modal（與 MetaTemplatePanel 的「模板內容」氣泡樣式一致）
 * 用於流程設計器各節點的「模板信息」旁，方便用戶查看模板參數
 * @param {Object} template - Meta 模板對象 { name, components: [{ type, text, format, buttons, example }] }
 * @param {boolean} open - 是否顯示
 * @param {Function} onCancel - 關閉回調
 * @param {Function} t - 翻譯函數
 */
const TemplatePreviewModal = ({ template, open, onCancel, t }) => {
  if (!template || !template.components) {
    return (
      <Modal
        title={t('workflowDesigner.templatePreview') || '模板預覽'}
        open={open}
        onCancel={onCancel}
        footer={null}
        width={420}
      >
        <div style={{ padding: 24, color: '#999', textAlign: 'center' }}>
          {t('workflowDesigner.templatePreviewNoData') || '無模板資料可預覽'}
        </div>
      </Modal>
    );
  }

  const bubbleStyle = {
    maxWidth: '85%',
    width: '100%',
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    position: 'relative',
  };

  const renderHeader = () => {
    const headerComponent = template.components.find(c => c.type === 'HEADER');
    if (!headerComponent) return null;
    const format = (headerComponent.format || '').toUpperCase();
    if (format === 'IMAGE') {
      const headerExample = headerComponent.example;
      const imageUrl = headerExample?._preview_url || headerExample?.header_url ||
        (headerExample?.header_handle?.[0] && !headerExample.header_handle[0].startsWith('4:') ? headerExample.header_handle[0] : null);
      const hasValidUrl = imageUrl && !imageUrl.startsWith('4:') && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'));
      return (
        <div style={{ width: '100%', background: '#f0f0f0' }}>
          <div style={{
            width: '100%', aspectRatio: '16/9', background: '#e0e0e0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12,
          }}>
            {hasValidUrl ? (
              <img src={imageUrl} alt="Header" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <span><FileImageOutlined style={{ marginRight: 8 }} />圖片 Header</span>
            )}
          </div>
        </div>
      );
    }
    if (format === 'VIDEO') {
      return (
        <div style={{ padding: 16, background: '#f0f0f0', display: 'flex', alignItems: 'center', gap: 8, color: '#999', fontSize: 12 }}>
          <VideoCameraOutlined /> 影片 Header
        </div>
      );
    }
    if (format === 'DOCUMENT') {
      return (
        <div style={{ padding: 16, background: '#f0f0f0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <FileOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          <span>文件</span>
        </div>
      );
    }
    if (format === 'TEXT' && headerComponent.text) {
      return (
        <div style={{ padding: '12px 16px', background: '#f0f0f0', borderBottom: '1px solid #e0e0e0', fontSize: 14, fontWeight: 'bold', whiteSpace: 'pre-wrap' }}>
          {headerComponent.text.replace(/\{\{(\d+)\}\}/g, (m, n) => `{{${n}}}`)}
        </div>
      );
    }
    if (format === 'LOCATION') {
      return (
        <div style={{ padding: 16, background: '#e8f5e9', display: 'flex', alignItems: 'center', gap: 8, color: '#4caf50', fontSize: 12 }}>
          <EnvironmentOutlined /> 位置 Header
        </div>
      );
    }
    return null;
  };

  const renderBody = () => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    if (!bodyComponent?.text) return null;
    let formattedText = bodyComponent.text;
    formattedText = formattedText.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    formattedText = formattedText.replace(/_([^_]+)_/g, '<em>$1</em>');
    formattedText = formattedText.replace(/~([^~]+)~/g, '<del>$1</del>');
    formattedText = formattedText.replace(/```([^`]+)```/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;">$1</code>');
    return (
      <div
        style={{ padding: '12px 16px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#111b21' }}
        dangerouslySetInnerHTML={{ __html: formattedText }}
      />
    );
  };

  const renderFooter = () => {
    const footerComponent = template.components.find(c => c.type === 'FOOTER');
    if (!footerComponent?.text) return null;
    return (
      <div style={{ padding: '8px 16px', fontSize: 12, color: '#667781', borderTop: '1px solid #e0e0e0', background: '#f9f9f9' }}>
        {footerComponent.text}
      </div>
    );
  };

  const renderButtons = () => {
    const buttonsComponent = template.components.find(c => c.type === 'BUTTONS');
    if (!buttonsComponent?.buttons?.length) return null;
    return (
      <div style={{ padding: 8, borderTop: '1px solid #e0e0e0', background: '#f9f9f9' }}>
        {buttonsComponent.buttons.map((button, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: idx < buttonsComponent.buttons.length - 1 ? 8 : 0,
              padding: '10px 12px',
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            {button.type === 'QUICK_REPLY' && '💬 '}
            {button.type === 'URL' && '🔗 '}
            {button.type === 'PHONE_NUMBER' && '📞 '}
            {button.text}
          </div>
        ))}
      </div>
    );
  };

  const titleText = typeof t === 'function' ? (t('workflowDesigner.templateContent') || '模板內容') : '模板內容';

  return (
    <Modal
      title={t('workflowDesigner.templatePreview') || '模板預覽'}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={420}
      destroyOnClose
    >
      <Card title={titleText} size="small" style={{ marginBottom: 0 }}>
        <div style={{
          background: 'linear-gradient(to bottom, #e5ddd5 0%, #e5ddd5 50%, #d4edda 50%, #d4edda 100%)',
          padding: '24px 16px',
          borderRadius: 8,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
        >
          <div style={bubbleStyle}>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
            {renderButtons()}
            <div style={{ padding: '4px 16px 8px', fontSize: 11, color: '#667781', textAlign: 'right' }}>
              10:40
            </div>
          </div>
        </div>
      </Card>
    </Modal>
  );
};

export default TemplatePreviewModal;
